import React, { useCallback, useEffect, useRef } from 'react';

/**
 * Manages window-level mousemove/mouseup listeners for drag interactions.
 * Returns a `startDrag` function to call from an onMouseDown handler.
 * Listeners are automatically removed on mouseup and on component unmount.
 *
 * @param onMove - Called on every mousemove while dragging.
 * @param onEnd  - Called once on mouseup to commit the drag result.
 */
export function useWindowMouseDrag(
    onMove: (e: MouseEvent) => void,
    onEnd: (e: MouseEvent) => void
): (e: React.MouseEvent) => void {
    // Refs so the stable wrappers always call the latest callbacks without
    // needing to be recreated (avoids re-registering listeners on every render).
    const onMoveRef = useRef(onMove);
    const onEndRef = useRef(onEnd);
    onMoveRef.current = onMove;
    onEndRef.current = onEnd;

    // Stable handler refs — created once, never reassigned.
    const handleMove = useRef((e: MouseEvent) => onMoveRef.current(e));
    const handleEnd = useRef((e: MouseEvent) => {
        onEndRef.current(e);
        window.removeEventListener('mousemove', handleMove.current);
        window.removeEventListener('mouseup', handleEnd.current);
    });

    useEffect(() => {
        const move = handleMove.current;
        const end = handleEnd.current;
        return () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', end);
        };
    }, []);

    return useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        window.addEventListener('mousemove', handleMove.current);
        window.addEventListener('mouseup', handleEnd.current);
    }, []);
}
